import { useCallback, useEffect, useRef, useState } from 'react';
import { supabase } from '../lib/supabase.js';
import { NOTABLE_GOLD, VILLAGE_COLOR } from '../data/regionColors.js';
import { searchStaticIndex } from '../utils/staticSearch.js';
import { searchGeoFeatures } from '../utils/geoSearch.js';
import { VineIcon, BottleIcon, HouseIcon, MountainIcon } from './icons.jsx';
import { ANBAUGEBIETE } from '../data/regionsAndBereiche.js';

// Anonymous search runs against the build-time index on the CDN
// (/regions-data/search-index.json) — same static-first pattern as the
// map data. Loaded lazily on the first query, cached for the session;
// any miss (dev without a build, thin file) falls back to search_all.
let indexPromise = null;
function loadSearchIndex() {
  if (!indexPromise) {
    indexPromise = fetch('/regions-data/search-index.json')
      .then(res => (res.ok ? res.json() : null))
      // atlas guard: the baked index is per-country — a foreign atlas's
      // file (German bake under a michigan server) must miss so search
      // falls through to the live RPC instead of offering dead results
      .then(json => (Array.isArray(json?.entries) && json.entries.length > 0
        && json.entries.every(e => ANBAUGEBIETE.includes(e.anbaugebiet)) ? json.entries : null))
      .catch(() => null);
  }
  return indexPromise;
}

export default function SearchBar({ onSelectResult, onClose, autoFocus, staticFirst = false }) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [highlighted, setHighlighted] = useState(-1);
  const [loading, setLoading] = useState(false);
  const debounceRef = useRef(null);
  const wrapRef = useRef(null);
  const inputRef = useRef(null);

  // Auto-focus when mounted with autoFocus
  useEffect(() => {
    if (autoFocus && inputRef.current) inputRef.current.focus();
  }, [autoFocus]);

  // Close dropdown on click-away (but keep search bar open)
  useEffect(() => {
    const handler = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const search = useCallback(async (q) => {
    if (q.length < 2) { setResults([]); setOpen(false); return; }
    setLoading(true);
    try {
      if (staticFirst) {
        const entries = await loadSearchIndex();
        if (entries) {
          setResults([...searchGeoFeatures(q), ...searchStaticIndex(entries, q, 30)]);
          setOpen(true);
          setHighlighted(-1);
          return;
        }
      }
      const { data, error } = await supabase.rpc('search_all', { p_query: q, p_limit: 30 });
      if (error) throw error;
      // Deduplicate by kind+id
      const seen = new Set();
      const unique = (data ?? []).filter(r => {
        const key = `${r.kind}-${r.id}`;
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      });
      // Key Rivers & Mountains are a fixed client-side list — lead with them
      setResults([...searchGeoFeatures(q), ...unique]);
      setOpen(true);
      setHighlighted(-1);
    } catch (e) {
      console.warn('[SearchBar]', e.message);
      setResults([]);
    } finally {
      setLoading(false);
    }
  }, [staticFirst]);

  const handleChange = (e) => {
    const val = e.target.value;
    setQuery(val);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (val.length < 2) { setResults([]); setOpen(false); return; }
    debounceRef.current = setTimeout(() => search(val), 300);
  };

  const selectItem = (item) => {
    setQuery('');
    setResults([]);
    setOpen(false);
    onSelectResult(item);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Escape') {
      setOpen(false);
      if (onClose) onClose();
      return;
    }
    if (!open || results.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlighted(h => (h + 1) % results.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlighted(h => (h - 1 + results.length) % results.length);
    } else if (e.key === 'Enter' && highlighted >= 0) {
      e.preventDefault();
      selectItem(results[highlighted]);
    }
  };

  const iconForResult = (r) => {
    if (r.kind === 'vineyard') {
      if (r.notable) {
        return <span className="search-icon search-icon--star" style={{ color: NOTABLE_GOLD }}><VineIcon /></span>;
      }
      if (r.vdp_gg_eligible || r.official_grosses_gewaechs_eligible) {
        return <span className="search-icon search-icon--gg">GG</span>;
      }
      return <span className="search-icon search-icon--vineyard" style={{ color: '#16a34a' }}><VineIcon size={14} /></span>;
    }
    if (r.kind === 'village') {
      return <span className="search-icon search-icon--village" style={{ color: VILLAGE_COLOR }}><HouseIcon size={13} /></span>;
    }
    if (r.kind === 'producer') {
      return <span className="search-icon search-icon--producer"><BottleIcon size={16} /></span>;
    }
    if (r.kind === 'mountain') {
      return <span className="search-icon" style={{ color: '#6b7a63' }}><MountainIcon size={13} /></span>;
    }
    if (r.kind === 'river') {
      return (
        <span className="search-icon" style={{ color: '#4d94c4' }}>
          <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" aria-hidden="true">
            <path d="M3 7c3-2.5 6-2.5 9 0s6 2.5 9 0" />
            <path d="M3 13c3-2.5 6-2.5 9 0s6 2.5 9 0" />
            <path d="M3 19c3-2.5 6-2.5 9 0s6 2.5 9 0" />
          </svg>
        </span>
      );
    }
    return null;
  };

  return (
    <div className={`search-bar ${onClose ? 'search-bar--with-close' : ''}`} ref={wrapRef}>
      <svg className="search-bar__icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="11" cy="11" r="8" />
        <line x1="21" y1="21" x2="16.65" y2="16.65" />
      </svg>
      <input
        ref={inputRef}
        className="search-bar__input"
        type="text"
        placeholder="Search vineyards, villages, producers…"
        value={query}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        onFocus={() => { if (results.length > 0) setOpen(true); }}
      />
      {loading && <span className="search-bar__spinner" />}
      {onClose && (
        <button type="button" className="search-bar__close" onClick={onClose} aria-label="Close search">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>
      )}
      {open && results.length > 0 && (
        <ul className="search-bar__dropdown">
          {results.map((r, i) => (
            <li
              key={`${r.kind}-${r.id}`}
              className={`search-bar__item ${i === highlighted ? 'search-bar__item--active' : ''}`}
              onMouseDown={() => selectItem(r)}
              onMouseEnter={() => setHighlighted(i)}
            >
              {iconForResult(r)}
              <span className="search-bar__label">{r.label}</span>
              <span className="search-bar__context">
                {[r.kind === 'mountain' ? 'Mountain Range' : r.kind.charAt(0).toUpperCase() + r.kind.slice(1), r.anbaugebiet, r.ort].filter(Boolean).join(' | ')}
              </span>
            </li>
          ))}
        </ul>
      )}
      {open && results.length === 0 && query.length >= 2 && !loading && (
        <div className="search-bar__dropdown search-bar__empty">No results</div>
      )}
    </div>
  );
}
